<template>
  <section class="section">
    <h2 class="title">
      {{ $t('route.indexes') }}
      <div class="level">
        <div class="level-left subtitle">
          {{ $t('subtitle.indexes') }}
        </div>
        <div class="level-right">
          <div class="field is-grouped">
              <div class="control">
              <button class="button is-primary" @click.prevent="create">
                <octicon :icon="plus" /><span>{{ $t('newIndex') }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </h2>
    <box>
      <data-table
        ref="table"
        :data="tableData"
        :columns="tableColumns"
        :loading="isTableLoading"
        :searchable="true"
        :search-placeholder="$t('indexes.searchPlaceholder')"
        :items-per-page-options="[10, 15, 30, 50]"
        :default-items-per-page="10"
        :default-sort-by="'created_at'"
        :default-sort-order="'desc'"
        :server-side="true"
        :total-items="totalItems"
        @search="handleSearch"
        @sort="handleSort"
        @page-change="handlePageChange"
        @page-size-change="handlePageSizeChange"
      >
        <!-- STT Column -->
        <template #cell-stt="{ index }">
          {{ index + 1 }}
        </template>

        <!-- Name Column -->
        <template #cell-name="{ value }">
          <div class="truncate-cell" :title="value">{{ value }}</div>
        </template>

        <!-- Description Column: uses DataTable's truncate-cell class -->
        <template #cell-description="{ value }">
          <div class="truncate-cell" :title="value || '-'">{{ value || '-' }}</div>
        </template>

        <!-- Size Column -->
        <template #cell-size="{ row }">
          <span>{{ row.current_size_string || '0 B' }}</span> / <span>{{ row.max_size }} </span><span class="is-uppercase">{{ row.unit }}</span>
        </template>

        <!-- Storage Duration Column -->
        <template #cell-storage_duration="{ row }">
          <span>{{ formatStorageDuration(row.storage_duration) }}</span> / <span v-if="row.rotate_time && row.rotate_time > 0">{{ row.rotate_time }} {{ $t('indexes.unitDays') }}</span><span v-else>{{ $t('indexes.unlimited') }}</span>
        </template>

        <!-- Created At Column -->
        <template #cell-created_at="{ value }">
          <span v-if="value">{{ formatDate(value) }}</span>
          <span v-else>-</span>
        </template>

        <!-- Actions Column -->
        <template #cell-actions="{ row }">
          <div class="has-text-centered">
            <button class="button is-text" @click.prevent="edit(row)" :title="$t('indexes.actions.update')">
              <octicon :icon="pencil" />
            </button>
            <button class="button is-text" @click.prevent="destroy(row)" :title="$t('delete')">
              <octicon :icon="x" />
            </button>
          </div>
        </template>
      </data-table>
    </box>

    <!-- Index Form Modal -->
    <index-form-modal
      :show.sync="modal"
      :is-edit-mode="isedit"
      :edit-item="item"
      :existing-indexes="existingIndexes"
      @success="handleFormSuccess"
    />

    <!-- Index Dependency Warning Dialog -->
    <index-dependency-warning
      :show.sync="showDependencyWarning"
      :data-input-names="dependencyDataInputNames"
      :forwarder-names="dependencyForwarderNames"
      @confirm="handleDependencyWarningConfirm"
      @cancel="handleDependencyWarningCancel"
    />

    <!-- Index Delete Confirmation Dialog -->
    <confirmation-dialog
      :show="showDeleteConfirmation"
      :title="deleteConfirmationTitle"
      :message="deleteConfirmationMessage"
      :warning-text="$t('indexes.deleteConfirmation.warning')"
      :cancel-text="$t('indexes.deleteConfirmation.cancel')"
      :confirm-text="$t('indexes.deleteConfirmation.confirm')"
      confirm-button-class="is-danger"
      :show-countdown="true"
      :countdown-seconds="5"
      :countdown-message-template="$t('indexes.deleteConfirmation.countdown')"
      :data-input-names="dependencyDataInputNames"
      :forwarder-names="dependencyForwarderNames"
      :dependency-warning-message="$t('indexes.deleteConfirmation.dependencyWarning')"
      :data-input-label="$t('indexes.deleteConfirmation.dataInputLabel')"
      :forwarder-label="$t('indexes.deleteConfirmation.forwarderLabel')"
      @confirm="handleDeleteConfirm"
      @cancel="handleDeleteCancel"
    />

  </section>
</template>
<script>
import { Modal, Popover } from '@cyradar/ui'
import DataTable from '@/components/common/DataTable.vue'
import { plus, x, pencil, info } from 'octicons-vue'
import IndexDependencyWarning from '@/components/indexes/IndexDependencyWarning.vue'
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue'
import IndexFormModal from '@/components/indexes/IndexFormModal.vue'

const units = ['gb', 'tb', 'mb']

export default {
  components: { DataTable, Modal, Popover, IndexDependencyWarning, ConfirmationDialog, IndexFormModal },
  data () {
    return {
      modal: false,
      isedit: true,
      item: null,
      showFilter: false,
      actualFilteredCount: 0,
      existingIndexes: [],
      // Dependency warning dialog properties
      showDependencyWarning: false,
      dependencyDataInputNames: [],
      dependencyForwarderNames: [],
      // Delete confirmation dialog properties
      showDeleteConfirmation: false,
      itemToDelete: null,
      // DataTable properties
      tableData: [],
      isTableLoading: false,
      totalItems: 0,
      searchQuery: '',
      sortBy: 'created_at',
      sortOrder: 'desc',
      currentPage: 1,
      itemsPerPage: 10
    }
  },
  computed: {
    plus () {
      return plus
    },
    x () {
      return x
    },
    pencil () {
      return pencil
    },
    info () {
      return info
    },
    units () {
      return units
    },
    /**
     * Check if any filters are currently active
     */
    hasActiveFilters() {
      return false
    },
    activeFilterCount() {
      return 0
    },
    /**
     * Check if item has dependencies for delete
     */
    hasDependenciesForDelete() {
      return this.dependencyDataInputNames.length > 0 || this.dependencyForwarderNames.length > 0
    },
    // Delete confirmation title
    deleteConfirmationTitle() {
      const indexName = this.itemToDelete ? this.itemToDelete.name : ''
      return this.hasDependenciesForDelete 
        ? this.$t('indexes.deleteConfirmation.warningTitle') 
        : this.$t('indexes.deleteConfirmation.title', { indexName })
    },
    // Delete confirmation message
    deleteConfirmationMessage() {
      return this.$t('indexes.deleteConfirmation.message')
    },
    /**
     * Table columns configuration
     */
    tableColumns() {
      return [
        {
          key: 'stt',
          label: 'STT',
          field: 'stt',
          width: '5%',
          sortable: false,
          align: 'center'
        },
        {
          key: 'name',
          label: this.$t('indexes.columnLabels.name'),
          field: 'name',
          width: '15%',
          sortable: true,
          truncate: true
        },
        {
          key: 'description',
          label: this.$t('description'),
          field: 'description',
          width: '20%',
          sortable: false,
          truncate: true
        },
        {
          key: 'size',
          label: this.$t('indexes.columnLabels.size'),
          field: 'current_size',
          width: '15%',
          sortable: true
        },
        {
          key: 'storage_duration',
          label: this.$t('indexes.columnLabels.storageDuration'),
          field: 'storage_duration',
          width: '15%',
          sortable: false
        },
        {
          key: 'created_at',
          label: this.$t('indexes.columnLabels.createdAt'),
          field: 'created_at',
          width: '12%',
          sortable: true
        },
        {
          key: 'actions',
          label: this.$t('indexes.columnLabels.actions'),
          field: 'actions',
          width: '13%',
          sortable: false,
          align: 'center'
        }
      ]
    }
  },
  mounted() {
    // Load existing indexes for validation
    this.loadExistingIndexes()
    // Load initial data
    this.loadTableData()

    // Prevent password manager popup by adding hidden dummy inputs
    this.$nextTick(() => {
      const searchInput = this.$el.querySelector('input[type="search"], input[placeholder*="Search"], input[placeholder*="Tìm kiếm"]')
      if (searchInput && searchInput.parentElement) {
        // Create hidden dummy inputs to mislead password managers
        const dummyUsername = document.createElement('input')
        dummyUsername.setAttribute('type', 'text')
        dummyUsername.setAttribute('name', 'fake_username_' + Math.random())
        dummyUsername.style.position = 'absolute'
        dummyUsername.style.top = '-9999px'
        dummyUsername.style.left = '-9999px'
        dummyUsername.setAttribute('tabindex', '-1')
        dummyUsername.setAttribute('aria-hidden', 'true')

        const dummyPassword = document.createElement('input')
        dummyPassword.setAttribute('type', 'password')
        dummyPassword.setAttribute('name', 'fake_password_' + Math.random())
        dummyPassword.style.position = 'absolute'
        dummyPassword.style.top = '-9999px'
        dummyPassword.style.left = '-9999px'
        dummyPassword.setAttribute('tabindex', '-1')
        dummyPassword.setAttribute('aria-hidden', 'true')

        // Insert dummy inputs before the search input
        searchInput.parentElement.insertBefore(dummyUsername, searchInput)
        searchInput.parentElement.insertBefore(dummyPassword, searchInput)
      }
    })
  },
  methods: {
    /**
     * Loads existing indexes for uniqueness validation
     */
    async loadExistingIndexes() {
      try {
        const response = await this.$http.get('/api/v1/indexes', { params: { limit: 1000 } })
        if (response && response.data && response.data.data && response.data.data.items) {
          this.existingIndexes = response.data.data.items.map(index => index.name)
        }
      } catch (error) {
        console.warn('Failed to load existing indexes for validation:', error)
      }
    },

    formatDate (dateString) {
      if (!dateString || dateString === '0001-01-01T00:00:00Z') return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
    },
    /**
     * Format storage duration object to display string
     * @param {Object} duration - Object with days and hours properties
     * @returns {string} Formatted string like "16 Days 12 Hours" or "16 Ngày 12 Giờ"
     */
    formatStorageDuration(duration) {
      if (!duration) {
        return `0 ${this.$t('indexes.unitDays')}`
      }

      const days = duration.days || 0
      const hours = duration.hours || 0

      // If both are 0, return "0 Days"
      if (days === 0 && hours === 0) {
        return `0 ${this.$t('indexes.unitDays')}`
      }

      let parts = []

      // Add days part
      if (days > 0) {
        const dayUnit = days === 1 ? this.$t('indexes.unitDay') : this.$t('indexes.unitDays')
        parts.push(`${days} ${dayUnit}`)
      }

      // Add hours part
      if (hours > 0) {
        const hourUnit = hours === 1 ? this.$t('indexes.unitHour') : this.$t('indexes.unitHours')
        parts.push(`${hours} ${hourUnit}`)
      }

      return parts.join(' ')
    },
    handleFormSuccess() {
      // Called when form is successfully submitted
      this.loadTableData()
    },
    create () {
      this.isedit = false
      this.item = {
        unit: 'gb',
        max_size: 10,
        rotate_time: 90,
        name: '',
        description: ''
      }
      this.modal = true
    },
    edit (item) {
      this.item = JSON.parse(JSON.stringify(item))
      this.isedit = true
      this.modal = true
    },
    /**
     * Load table data from API
     */
    async loadTableData() {
      this.isTableLoading = true

      const params = {
        filter: this.searchQuery,
        sort: this.sortBy,
        order: this.sortOrder,
        page: this.currentPage,
        limit: this.itemsPerPage
      }

      try {
        const response = await this.$http.get('/api/v1/indexes', { params })
        if (response && response.data && response.data.data) {
          this.tableData = response.data.data.items || []
          this.totalItems = response.data.data.total || 0
        }
      } catch (error) {
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          error
        })
      } finally {
        this.isTableLoading = false
      }
    },
    /**
     * Handle search event from DataTable
     */
    handleSearch(query) {
      this.searchQuery = query
      this.currentPage = 1
      this.loadTableData()
    },
    /**
     * Handle sort event from DataTable
     */
    handleSort(sortData) {
      this.sortBy = sortData.by
      this.sortOrder = sortData.order
      this.loadTableData()
    },
    /**
     * Handle page change event from DataTable
     */
    handlePageChange(page) {
      this.currentPage = page
      this.loadTableData()
    },
    /**
     * Handle page size change event from DataTable
     */
    handlePageSizeChange(pageSize) {
      this.itemsPerPage = pageSize
      this.currentPage = 1
      this.loadTableData()
    },
    async destroy (item) {
      this.itemToDelete = item

      // Check dependencies before showing confirmation
      try {
        const response = await this.$http.get(`/api/v1/indexes/${item.id}/dependencies`)
        if (response && response.data && response.data.data) {
          this.dependencyDataInputNames = response.data.data.data_input_names || []
          this.dependencyForwarderNames = response.data.data.forwarder_names || []
        }
      } catch (error) {
        console.error('Failed to check dependencies:', error)
        this.dependencyDataInputNames = []
        this.dependencyForwarderNames = []
      }

      this.showDeleteConfirmation = true
    },

    /**
     * Handles the confirmation of index deletion
     */
    handleDeleteConfirm() {
      if (!this.itemToDelete) {
        return
      }

      return this.$http.delete(`/api/v1/indexes/${this.itemToDelete.id}`).then(body => {
        this.$store.dispatch('NOTIFY', {
          type: 'success',
          text: this.$t('indexes.deleteSuccess')
        })

        this.loadTableData()
      }).catch(error => {
        // Handle dependency error (HTTP 409 Conflict)
        if (error.response && error.response.status === 409) {
          this.handleDependencyError(error.response.data.message)
        } else {
          this.$store.dispatch('NOTIFY', {
            type: 'error',
            text: error.response?.data?.message || 'Xóa index thất bại'
          })
        }
      }).finally(() => {
        // Close dialog and clear the item to delete and dependency data
        this.showDeleteConfirmation = false
        this.itemToDelete = null
        this.dependencyDataInputNames = []
        this.dependencyForwarderNames = []
      })
    },

    /**
     * Handles the cancellation of index deletion
     */
    handleDeleteCancel() {
      this.showDeleteConfirmation = false
      this.itemToDelete = null
      // Clear dependency data
      this.dependencyDataInputNames = []
      this.dependencyForwarderNames = []
    },

    /**
     * Handle dependency error by parsing the message and showing the warning dialog
     */
    handleDependencyError(message) {
      // Parse the Vietnamese error message to extract dependency names
      const dataInputMatch = message.match(/DataInput:\s*([^,]+(?:,\s*[^,]+)*)/i)
      const forwarderMatch = message.match(/Forwarder:\s*([^,]+(?:,\s*[^,]+)*)/i)

      this.dependencyDataInputNames = dataInputMatch
        ? dataInputMatch[1].split(',').map(name => name.trim()).filter(name => name)
        : []

      this.dependencyForwarderNames = forwarderMatch
        ? forwarderMatch[1].split(',').map(name => name.trim()).filter(name => name)
        : []

      this.showDependencyWarning = true
    },

    /**
     * Handle confirmation of dependency warning dialog
     */
    handleDependencyWarningConfirm() {
      this.showDependencyWarning = false
      this.dependencyDataInputNames = []
      this.dependencyForwarderNames = []
    },

    /**
     * Handle cancellation of dependency warning dialog
     */
    handleDependencyWarningCancel() {
      this.showDependencyWarning = false
      this.dependencyDataInputNames = []
      this.dependencyForwarderNames = []
    }
  }
}
</script>

<style lang="scss" scoped>
.ml-1 {
  margin-left: 0.25rem;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.search-help-container {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
</style>
